import { useState, useEffect, useCallback, useRef } from 'react';
import { db, functions } from '../firebase';
import { collection, query, where, doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// The server combines legacy subscriptions and owns all quota changes.
const useSubscription = (user) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const uid = user?.uid;
  const requestVersion = useRef(0);

  const refreshSubscription = useCallback(async () => {
    const version = ++requestVersion.current;
    if (!uid) { setSubscription(null); setLoading(false); return null; }
    try {
      const result = await httpsCallable(functions, 'getActiveSubscription')();
      const data = result.data.subscription;
      const normalized = data ? {
        ...data, maxDownloads: data.isUnlimited ? 999999 : 10, totalDownloads: data.isUnlimited ? 999999 : 10,
        daysRemaining: data.expiresAt ? Math.max(0, Math.ceil((new Date(data.expiresAt) - Date.now()) / 86400000)) : 999
      } : null;
      if (version === requestVersion.current) { setSubscription(normalized); setError(null); }
      return normalized;
    } catch (err) {
      if (version === requestVersion.current) {
        setSubscription(null); setError('구독 정보를 불러오지 못했습니다. 다시 시도해주세요.');
      }
      return null;
    } finally { if (version === requestVersion.current) setLoading(false); }
  }, [uid]);

  const invalidateRequests = useCallback(() => { ++requestVersion.current; }, []);

  useEffect(() => {
    setSubscription(null); setLoading(!!uid); setError(null);
    if (!uid) { refreshSubscription(); return; }
    let timer;
    const changed = () => { clearTimeout(timer); timer = setTimeout(refreshSubscription, 100); };
    const failed = () => { setError('구독 정보 갱신에 실패했습니다.'); setLoading(false); };
    const stopUser = onSnapshot(doc(db, 'users', uid), changed, failed);
    const stopSubscriptions = onSnapshot(query(collection(db, 'subscriptions'), where('userId', '==', uid)), changed, failed);
    return () => { invalidateRequests(); clearTimeout(timer); stopUser(); stopSubscriptions(); };
  }, [uid, refreshSubscription, invalidateRequests]);

  const canDownload = () => !!subscription && subscription.status === 'ACTIVE' &&
    (!subscription.expiresAt || new Date(subscription.expiresAt) > new Date()) &&
    (subscription.isUnlimited || subscription.remainingDownloads > 0);
  const canDownloadCategory = category => canDownload() && subscription.allowedCategories.includes(category);
  // Compatibility for callers that validate before requesting the file.
  // The download endpoint atomically checks and consumes the quota, exactly once.
  const downloadPDF = async () => {
    const current = await refreshSubscription();
    if (!current) throw new Error('유효한 구독이 필요합니다.');
    return { success: true, remainingDownloads: current.remainingDownloads };
  };
  const getSubscriptionStatus = () => !subscription ? 'NONE' : canDownload() ? 'ACTIVE' : 'EXPIRED';
  const getStatusMessage = () => error || (!subscription ? '구독 플랜이 필요합니다.' :
    !canDownload() ? '구독이 만료되었습니다.' : subscription.isUnlimited ? '다운로드 무제한' : `${subscription.remainingDownloads}회 다운로드 가능`);
  const getDaysRemaining = () => subscription?.daysRemaining || 0;
  const getUsagePercentage = () => !subscription || subscription.isUnlimited ? 0 :
    Math.max(0, Math.round((subscription.maxDownloads - subscription.remainingDownloads) / subscription.maxDownloads * 100));

  return {
    subscription, loading, error, downloadPDF, refreshSubscription, canDownload, canDownloadCategory,
    getSubscriptionStatus, getStatusMessage, getDaysRemaining, getUsagePercentage,
    isActive: canDownload(), isExpired: getSubscriptionStatus() === 'EXPIRED', hasSubscription: !!subscription,
    downloadsRemaining: subscription?.remainingDownloads || 0, totalDownloads: subscription?.maxDownloads || 0,
    planName: subscription?.planName || '', planType: subscription?.planType || '', isUnlimited: !!subscription?.isUnlimited
  };
};

export default useSubscription;
